import { CalendarIcon, ClockIcon } from "@radix-ui/react-icons";
import { addDays, endOfDay, format, isSameDay, isSameYear, startOfDay } from "date-fns";
import { X } from "lucide-react";
import { DateRange, SelectRangeEventHandler, SelectSingleEventHandler } from "react-day-picker";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export const getDatePickerPresets = (maxDays?: number) => {
  const now = new Date();
  const today = new Date(now);
  const todayStart = new Date(today.setHours(0, 0, 0, 0));
  const todayEnd = new Date(new Date().setHours(23, 59, 59, 999));

  if (!maxDays || maxDays >= 30) {
    return {
      Today: {
        from: todayStart,
        to: todayEnd,
      },
      "Last 7 days": {
        from: addDays(new Date(), -7),
        to: todayEnd,
      },
      "Last 14 days": {
        from: addDays(new Date(), -14),
        to: todayEnd,
      },
      "Last 30 days": {
        from: addDays(new Date(), -30),
        to: todayEnd,
      },
      "All Time": {
        from: new Date(0),
        to: todayEnd,
      },
    };
  }

  // For limited range (e.g., 7 days), only show presets that fit
  const presets: Record<string, { from: Date; to: Date }> = {
    Today: {
      from: todayStart,
      to: todayEnd,
    },
  };

  if (maxDays >= 3) {
    presets["Last 3 days"] = {
      from: addDays(new Date(), -2),
      to: todayEnd,
    };
  }

  if (maxDays >= 7) {
    presets["Last 7 days"] = {
      from: addDays(new Date(), -6),
      to: todayEnd,
    };
  }

  return presets;
};

export const datePickerPresets = getDatePickerPresets();

export type Props = {
  isLoading?: boolean;
  date: DateRange | undefined;
  setDate: (date: DateRange | undefined) => void;
  datetime?: boolean;
  includeSeconds?: boolean;
  removable?: boolean;
  hidePresets?: boolean;
  singleDateSelection?: boolean;
  showApplyButton?: boolean;
  applyDisabled?: boolean;
  onApply?: () => void;
  maxRange?: number;
  minDate?: Date;
  maxDate?: Date;
  /** When provided, use these presets instead of getDatePickerPresets(maxRange). Enables request-log style "Last 5 min" etc. */
  customPresets?: Record<string, { from: Date; to: Date }>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  triggerVariant?: "default" | "tinyIcon";
  triggerClassName?: string;
  showTriggerIndicator?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const PRESET_TIMESTAMP_MATCH_TOLERANCE_MS = 2000;
const doesDateRangeMatchPreset = (date: DateRange, preset: { from: Date; to: Date }): boolean => {
  if (!date.from || !date.to) {
    return false;
  }

  return (
    Math.abs(date.from.getTime() - preset.from.getTime()) < PRESET_TIMESTAMP_MATCH_TOLERANCE_MS &&
    Math.abs(date.to.getTime() - preset.to.getTime()) < PRESET_TIMESTAMP_MATCH_TOLERANCE_MS
  );
};

const getDateFormat = (
  datetime: boolean,
  includeSeconds: boolean,
  includeYear: boolean,
): string => {
  if (!datetime) {
    return includeYear ? "LLL dd, y" : "LLL dd";
  }

  if (includeSeconds) {
    return includeYear ? "LLL dd, y h:mm:ss a" : "LLL dd, h:mm:ss a";
  }

  return "LLL dd, h:mm a";
};

const clampDateToBounds = (value: Date, minDate?: Date, maxDate?: Date): Date => {
  if (minDate && value.getTime() < minDate.getTime()) {
    return new Date(minDate);
  }
  if (maxDate && value.getTime() > maxDate.getTime()) {
    return new Date(maxDate);
  }
  return value;
};

const DateDisplay = ({
  date,
  datetime,
  includeSeconds,
}: {
  date: DateRange | undefined;
  datetime: boolean;
  includeSeconds: boolean;
}) => {
  if (!date?.from || isNaN(date.from.getTime())) {
    return <span>Pick a date{datetime ? " and time" : ""}</span>;
  }

  const currentYear = new Date().getFullYear();

  if (date.to && !isNaN(date.to.getTime())) {
    const fromDateFormat = getDateFormat(
      datetime,
      includeSeconds,
      !isSameYear(date.from, date.to) || date.from.getFullYear() !== currentYear,
    );
    const toDateFormat = getDateFormat(
      datetime,
      includeSeconds,
      !isSameYear(date.from, date.to) || date.to.getFullYear() !== currentYear,
    );

    return (
      <>
        {format(date.from, fromDateFormat)} - {format(date.to, toDateFormat)}
      </>
    );
  }

  const fromDateFormat = getDateFormat(
    datetime,
    includeSeconds,
    date.from.getFullYear() !== currentYear,
  );
  return <>{format(date.from, fromDateFormat)}</>;
};

export function DatePickerWithRange({
  isLoading,
  hidePresets = false,
  className,
  date,
  setDate,
  datetime = false,
  includeSeconds = false,
  removable = true,
  showApplyButton = false,
  applyDisabled = false,
  onApply,
  singleDateSelection = false,
  maxRange,
  minDate,
  maxDate,
  customPresets,
  open,
  onOpenChange,
  triggerVariant = "default",
  triggerClassName,
  showTriggerIndicator = false,
}: Props) {
  const getDefaultFromTime = useCallback(
    () => (includeSeconds ? "00:00:00" : "00:00"),
    [includeSeconds],
  );
  const getDefaultToTime = useCallback(
    () => (includeSeconds ? "23:59:59" : "23:59"),
    [includeSeconds],
  );

  const [fromTime, setFromTime] = useState<string>(getDefaultFromTime());
  const [toTime, setToTime] = useState<string>(getDefaultToTime());
  const [displayMonth, setDisplayMonth] = useState<Date | undefined>(date?.from);
  // DayPicker compares full Date objects, so normalize bounds to calendar days.
  // The committed range still keeps the exact min/max timestamps for time inputs.
  const calendarMinDate = minDate ? startOfDay(minDate) : undefined;
  const calendarMaxDate = maxDate ? endOfDay(maxDate) : endOfDay(new Date());

  const basePresets = useMemo(
    () => customPresets ?? getDatePickerPresets(maxRange),
    [customPresets, maxRange],
  );

  const presetOptions = useMemo(
    () =>
      Object.keys(basePresets).map(label => ({
        id: label,
        value: label,
        label,
      })),
    [basePresets],
  );

  const matchingPreset = useMemo(() => {
    if (!date?.from || !date?.to) {
      return undefined;
    }
    for (const [key, preset] of Object.entries(basePresets)) {
      if (datetime || customPresets) {
        if (doesDateRangeMatchPreset(date, preset)) {
          return key;
        }
      } else if (isSameDay(date.from, preset.from) && isSameDay(date.to, preset.to)) {
        return key;
      }
    }
    return undefined;
  }, [date, basePresets, datetime, customPresets]);

  const handlePresetChange = useCallback(
    (value: string | null) => {
      const trimmedValue = value?.trim();
      if (!trimmedValue) {
        setDate(undefined);
        return;
      }

      const existingPreset = basePresets[trimmedValue];
      if (existingPreset) {
        setDate({
          from:
            minDate && existingPreset.from.getTime() < minDate.getTime()
              ? minDate
              : existingPreset.from,
          to:
            maxDate && existingPreset.to.getTime() > maxDate.getTime()
              ? maxDate
              : existingPreset.to,
        });
      }
    },
    [basePresets, maxDate, minDate, setDate],
  );

  useEffect(() => {
    if (date?.from && !isNaN(date.from.getTime())) {
      const hours = String(date.from.getHours()).padStart(2, "0");
      const minutes = String(date.from.getMinutes()).padStart(2, "0");
      const seconds = String(date.from.getSeconds()).padStart(2, "0");
      const timeString = includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
      setFromTime(timeString);
    } else {
      setFromTime(getDefaultFromTime());
    }

    if (date?.to && !isNaN(date.to.getTime())) {
      const hours = String(date.to.getHours()).padStart(2, "0");
      const minutes = String(date.to.getMinutes()).padStart(2, "0");
      const seconds = String(date.to.getSeconds()).padStart(2, "0");
      const timeString = includeSeconds ? `${hours}:${minutes}:${seconds}` : `${hours}:${minutes}`;
      setToTime(timeString);
    } else {
      setToTime(getDefaultToTime());
    }
  }, [date, includeSeconds, getDefaultToTime, getDefaultFromTime]);

  useEffect(() => {
    if (date?.from && !isNaN(date.from.getTime())) {
      setDisplayMonth(date.from);
    }
  }, [date?.from]);

  const handleSelect: SelectRangeEventHandler = (newDate, selectedDay) => {
    const isRangeAlreadySelected = date?.from && date?.to;
    if (isRangeAlreadySelected) {
      setDate({ from: selectedDay, to: undefined });
      return;
    }

    if (newDate?.from) {
      // Create a new date object to avoid mutations
      const updatedRange: DateRange = { from: undefined, to: undefined };

      if (datetime) {
        const timeParts = fromTime.split(":").map(Number);
        const [fromHours = 0, fromMinutes = 0, fromSeconds = 0] = timeParts;

        // Create a new date to avoid mutating the original
        const fromDate = new Date(newDate.from);
        fromDate.setHours(fromHours, fromMinutes, fromSeconds, 0);

        if (!isNaN(fromDate.getTime())) {
          updatedRange.from = clampDateToBounds(fromDate, minDate, maxDate);
        }

        if (newDate.to) {
          const toTimeParts = toTime.split(":").map(Number);
          const [toHours = 23, toMinutes = 59, toSeconds = 59] = toTimeParts;

          const toDate = new Date(newDate.to);
          toDate.setHours(toHours, toMinutes, toSeconds, 999);

          if (!isNaN(toDate.getTime())) {
            updatedRange.to = clampDateToBounds(toDate, minDate, maxDate);
          }
        }
      } else {
        // Set start time to beginning of day
        const fromDate = new Date(newDate.from);
        fromDate.setHours(0, 0, 0, 0);
        updatedRange.from = clampDateToBounds(fromDate, minDate, maxDate);

        if (newDate.to) {
          // Set end time to end of day to include all requests from that day
          const toDate = new Date(newDate.to);
          toDate.setHours(23, 59, 59, 999);
          updatedRange.to = clampDateToBounds(toDate, minDate, maxDate);
        }
      }

      setDate(updatedRange);
    } else {
      setDate(newDate);
    }
  };

  const handleSingleSelect: SelectSingleEventHandler = selectedDay => {
    if (!selectedDay) {
      setDate(undefined);
      return;
    }

    const nextDate = new Date(selectedDay);

    if (datetime) {
      const timeParts = fromTime.split(":").map(Number);
      const [hours = 0, minutes = 0, seconds = 0] = timeParts;
      nextDate.setHours(hours, minutes, seconds, 0);
    } else {
      nextDate.setHours(0, 0, 0, 0);
    }

    if (isNaN(nextDate.getTime())) {
      return;
    }

    setDate({ from: clampDateToBounds(nextDate, minDate, maxDate), to: undefined });
  };

  const handleFromTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFromTime(e.target.value);
    if (date?.from && !isNaN(date.from.getTime())) {
      const timeParts = e.target.value.split(":").map(Number);
      const [hours = 0, minutes = 0, seconds = 0] = timeParts;

      // Validate time values
      if (
        hours >= 0 &&
        hours < 24 &&
        minutes >= 0 &&
        minutes < 60 &&
        seconds >= 0 &&
        seconds < 60
      ) {
        const newFrom = new Date(date.from);
        newFrom.setHours(hours, minutes, seconds, 0);

        if (
          !isNaN(newFrom.getTime()) &&
          (!minDate || newFrom.getTime() >= minDate.getTime()) &&
          (!maxDate || newFrom.getTime() <= maxDate.getTime())
        ) {
          setDate({ ...date, from: newFrom });
        }
      }
    }
  };

  const handleToTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setToTime(e.target.value);
    if (date?.to && !isNaN(date.to.getTime())) {
      const timeParts = e.target.value.split(":").map(Number);
      const [hours = 23, minutes = 59, seconds = 59] = timeParts;

      // Validate time values
      if (
        hours >= 0 &&
        hours < 24 &&
        minutes >= 0 &&
        minutes < 60 &&
        seconds >= 0 &&
        seconds < 60
      ) {
        const newTo = new Date(date.to);
        newTo.setHours(hours, minutes, seconds, 999);

        if (
          !isNaN(newTo.getTime()) &&
          (!minDate || newTo.getTime() >= minDate.getTime()) &&
          (!maxDate || newTo.getTime() <= maxDate.getTime())
        ) {
          setDate({ ...date, to: newTo });
        }
      }
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDate(undefined);
  };

  if (isLoading) {
    return <Skeleton className="h-10 !w-[250px]" />;
  }
  const isTinyIconTrigger = triggerVariant === "tinyIcon";

  return (
    <div className={cn("grid gap-2", className)}>
      <div className="relative">
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <Button
              id="date"
              variant={isTinyIconTrigger ? "ghost" : "outline"}
              size={isTinyIconTrigger ? "tinyIcon" : undefined}
              className={cn(
                "relative",
                isTinyIconTrigger
                  ? "h-6 w-6 justify-center p-0 text-gray-400 transition-all duration-200 ease-in-out hover:bg-transparent hover:text-gray-500"
                  : "w-full min-w-[356px] justify-start text-left",
                !date && "text-muted-foreground",
                removable && !isTinyIconTrigger && "pr-8",
                triggerClassName,
              )}
              aria-label="Select date and time range"
            >
              <CalendarIcon
                className={cn("shrink-0", isTinyIconTrigger ? "h-3.5 w-3.5" : "mr-2 h-4 w-4")}
              />
              {!isTinyIconTrigger && (
                <span>
                  <DateDisplay date={date} datetime={datetime} includeSeconds={includeSeconds} />
                </span>
              )}
              {showTriggerIndicator && (
                <span className="pointer-events-none absolute right-0.5 top-0.5 h-2 w-2 rounded-full bg-blue-500 ring-1 ring-white" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex w-auto flex-col space-y-2 p-2">
            {!hidePresets && presetOptions.length > 0 && (
              <Select value={matchingPreset} onValueChange={handlePresetChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a preset" />
                </SelectTrigger>
                <SelectContent>
                  {presetOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {singleDateSelection ? (
              <Calendar
                initialFocus
                mode="single"
                month={displayMonth}
                onMonthChange={setDisplayMonth}
                selected={date?.from}
                onSelect={handleSingleSelect}
                numberOfMonths={2}
                fromDate={calendarMinDate}
                toDate={calendarMaxDate}
              />
            ) : (
              <Calendar
                initialFocus
                mode="range"
                month={displayMonth}
                onMonthChange={setDisplayMonth}
                selected={date}
                onSelect={handleSelect}
                numberOfMonths={2}
                fromDate={calendarMinDate}
                toDate={calendarMaxDate}
              />
            )}

            {datetime && (
              <div className="flex flex-col space-y-2 pt-2">
                <div className="flex items-center">
                  <ClockIcon className="mr-2 h-4 w-4" />
                  <span className="text-sm font-medium">Time</span>
                </div>
                <div
                  className={cn(
                    "gap-2",
                    singleDateSelection ? "grid grid-cols-1" : "grid grid-cols-2",
                  )}
                >
                  <div>
                    <label htmlFor="fromTime" className="text-xs text-muted-foreground">
                      {singleDateSelection ? "Time" : "From"}
                    </label>
                    <Input
                      id="fromTime"
                      type="time"
                      value={fromTime}
                      onChange={handleFromTimeChange}
                      className="mt-1"
                      step={includeSeconds ? 1 : undefined}
                    />
                  </div>
                  {!singleDateSelection && (
                    <div>
                      <label htmlFor="toTime" className="text-xs text-muted-foreground">
                        To
                      </label>
                      <Input
                        id="toTime"
                        type="time"
                        value={toTime}
                        onChange={handleToTimeChange}
                        className="mt-1"
                        step={includeSeconds ? 1 : undefined}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {showApplyButton && (
              <div className="flex justify-end border-t border-gray-100 pt-2">
                <Button size="sm" onClick={onApply} disabled={applyDisabled}>
                  Apply
                </Button>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {removable && date?.from && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="absolute right-3 top-1/2 ml-2 h-4 w-4 -translate-y-1/2 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>
    </div>
  );
}
